<script setup lang="ts">
import { computed } from 'vue'

import { useProjectStore } from '@/stores/project'
import { useProjectUserStore } from '@/stores/projectUser'
import { useAuthenticationStore } from '@/stores/authentication'
import { useAuthorizationStore } from '@/stores/authorization'
import { storeToRefs } from 'pinia'
import type { ProjectUserType } from '@/stores/type/projectUser.type'

import OptionsDrawer from '@/components/drawer/OptionsDrawer.vue'
import {
  AddUserDialog,
  DeleteDialog,
  UserManagementTable,
  UserManagementTitle
} from '@haskoning/vue-component-library'

const authorizationStore = useAuthorizationStore()
const projectStore = useProjectStore()
const projectUserStore = useProjectUserStore()
const authenticationStore = useAuthenticationStore()

const {
  items: projectUserItems,
  addUserDialog,
  writeLoading,
  deleteDialog,
  deleteLoading,
  deleteItem
} = storeToRefs(projectUserStore)
const { currentItem: currentProject } = storeToRefs(projectStore)
const { user: currentUser, searchResults, searchLoading } = storeToRefs(authenticationStore)
const { items: roles } = storeToRefs(authorizationStore)

const canAdd = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['add_projectuser']
  })
})

const canEdit = computed(() => {
  return authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: ['change_projectuser']
  })
})

const handleDelete = (item: ProjectUserType) => {
  projectUserStore.setDeleteItem(item)
  projectUserStore.openDeleteDialog()
}

const changeRole = async (payload: { item: any; newRole: number }) => {
  const { item, newRole } = payload
  if (item && newRole) {
    await projectUserStore.updateRole('project', item, newRole)
  }
}

const addUser = async (userId: number) => {
  if (currentProject.value && userId) {
    await projectUserStore.addUser('project', currentProject.value.id, userId)
  }
}

const searchUsers = async (search: string) => {
  if (currentProject.value) {
    await authenticationStore.searchUsers('project', currentProject.value.id, search)
  }
}
</script>

<template>
  <OptionsDrawer state-name="userManagementDrawer" :temporary="true">
    <UserManagementTitle
      class="pl-3 pb-1"
      :can-add="canAdd"
      @toggle-dialog="addUserDialog = true"
    />
    <v-divider />
    <v-lazy>
      <UserManagementTable
        v-if="currentUser"
        :current-user="currentUser"
        :roles="roles"
        :auth-model-users="projectUserItems"
        :can-edit="canEdit"
        auth-role="ProjectUser"
        @handleDelete="handleDelete"
        @set-selected-item="deleteItem = $event"
        @change-role="changeRole"
      />
    </v-lazy>
    <AddUserDialog
      :search-results="searchResults"
      :add-loading="writeLoading"
      :search-loading="searchLoading"
      v-model="addUserDialog"
      @add-user="addUser"
      @search-users="searchUsers"
      @reset-state="authenticationStore.resetSearchState()"
    />
    <DeleteDialog
      v-model="deleteDialog"
      :name="deleteItem?.email"
      :item="deleteItem"
      :loading="deleteLoading"
      @delete="projectUserStore.delete($event)"
    />
  </OptionsDrawer>
</template>
